import { useState, useRef, useEffect } from "react";
import { Play, Mail, Maximize2, FileText, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { mediaRaw } from "@/lib/api";
import { logEvent, sendDocument, getEmailStatus } from "@/lib/kioskData";
import { KioskButton, SectionLabel, TopBar, ScreenShell, useKiosk } from "../ui";

export function VideoScreen({ title, url, sources, description }) {
  const { back, setSuppressIdle } = useKiosk();
  const videoRef = useRef(null);
  const [error, setError] = useState(false);

  const srcs = (sources && sources.length) ? sources : (url ? [{ src: url }] : []);
  const key = srcs.map((s) => s.src).join("|");

  useEffect(() => { setError(false); }, [key]);
  useEffect(() => () => setSuppressIdle(false), [setSuppressIdle]);

  return (
    <ScreenShell>
      <TopBar title={title} onBack={back} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-6" data-testid="kiosk-video-player">
        <div className="relative overflow-hidden rounded-3xl glow-border bg-black">
          {error ? (
            <div className="flex h-72 flex-col items-center justify-center gap-3 text-slate-400">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
              <p>This video is currently unavailable.</p>
            </div>
          ) : srcs.length ? (
            <video
              ref={videoRef}
              key={key}
              data-testid="kiosk-video-element"
              controls
              playsInline
              preload="metadata"
              onPlay={() => setSuppressIdle(true)}
              onPause={() => setSuppressIdle(false)}
              onEnded={() => setSuppressIdle(false)}
              onError={(e) => { const c = e.currentTarget.error?.code; if (c && c !== 1) setError(true); }}
              className="h-[46vh] w-full bg-black"
            >
              {srcs.map((s, i) => <source key={i} src={s.src} type={s.type} />)}
            </video>
          ) : (
            <div className="flex h-72 items-center justify-center text-slate-400">No video source provided.</div>
          )}
        </div>
        {description && (
          <div className="mt-6">
            <SectionLabel>About this video</SectionLabel>
            <p className="mt-3 text-base leading-relaxed text-slate-300">{description}</p>
          </div>
        )}
      </div>
    </ScreenShell>
  );
}

export function PdfScreen({ title, mediaId, url, emailEnabled, solutionId }) {
  const { back, nav, setSuppressIdle } = useKiosk();
  const src = mediaId ? mediaRaw(mediaId) : url;
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setSuppressIdle(true); return () => setSuppressIdle(false); }, [setSuppressIdle]);

  const fullscreen = () => {
    const el = containerRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  return (
    <ScreenShell>
      <TopBar title={title} onBack={back} />
      <div className="min-h-0 flex-1 px-5 sm:px-8 py-6 flex flex-col overflow-hidden" data-testid="kiosk-pdf-viewer">
        <div ref={containerRef} className="relative flex-1 overflow-hidden rounded-3xl glow-border bg-[#0b1730] min-h-[52vh]">
          {!src ? (
            <div className="flex h-full items-center justify-center text-slate-400">Document unavailable.</div>
          ) : (
            <>
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center text-cyan-300">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              )}
              <iframe
                title={title}
                src={`${src}#view=FitH`}
                onLoad={() => setLoaded(true)}
                className="h-full min-h-[52vh] w-full"
                data-testid="kiosk-pdf-frame"
              />
            </>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {src && (
            <KioskButton testId="kiosk-pdf-fullscreen" variant="outline" onClick={fullscreen}>
              <Maximize2 className="h-5 w-5" /> Fit to Screen
            </KioskButton>
          )}
          {src && (
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              data-testid="kiosk-pdf-open"
              className="inline-flex items-center justify-center gap-2.5 rounded-2xl px-8 py-4 text-base sm:text-lg font-semibold tracking-wide glass text-white border border-cyan-400/40 glow-border-hover min-h-[56px] no-select"
            >
              <Download className="h-5 w-5" /> Open Original
            </a>
          )}
          {emailEnabled && mediaId && (
            <KioskButton
              testId="kiosk-pdf-email-cta"
              className="flex-1"
              onClick={() => nav("emailpdf", { title, mediaId, solutionId })}
            >
              <Mail className="h-5 w-5" /> Email this Document
            </KioskButton>
          )}
        </div>
      </div>
    </ScreenShell>
  );
}

export function EmailPdfScreen({ title, mediaId, solutionId }) {
  const { back, setSuppressIdle, branding } = useKiosk();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle|sending|sent|error
  const [message, setMessage] = useState("");
  const [configured, setConfigured] = useState(true);

  useEffect(() => { getEmailStatus().then(setConfigured); }, []);

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === "sending") return;
    setStatus("sending");
    setMessage("");
    try {
      await sendDocument({ email, media_id: mediaId, doc_title: title, solution_id: solutionId });
      logEvent("document_requested", title, solutionId);
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "We couldn't send the document. Please try again.");
    }
  };

  return (
    <ScreenShell>
      <TopBar title="Email Document" onBack={back} />
      <div className="min-h-0 flex-1 overflow-hidden px-5 sm:px-8 py-8 flex flex-col items-center" data-testid="kiosk-email-pdf-form">
        <div className="w-full max-w-md">
          <SectionLabel>Get the PDF on Email</SectionLabel>
          <h2 className="mt-2 font-head text-2xl sm:text-3xl font-bold text-white">{title}</h2>
          <p className="mt-2 text-sm text-slate-400">Enter your email and we'll send the document to your inbox instantly.</p>

          {!configured ? (
            <div className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 text-amber-100" data-testid="kiosk-email-not-configured">
              <AlertTriangle className="mb-2 h-6 w-6" />
              <p className="font-semibold">Email delivery is not configured yet.</p>
              <p className="mt-1 text-sm text-amber-200/80">Please ask our team at the booth for a copy of this document.</p>
            </div>
          ) : status === "sent" ? (
            <div className="mt-8 flex flex-col items-center gap-4 rounded-3xl glass glow-border p-8 text-center" data-testid="kiosk-email-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-400" />
              <h3 className="font-head text-2xl font-bold text-white">Sent successfully!</h3>
              <p className="text-sm text-slate-300">We've emailed <span className="cyan-text">{email}</span> a secure link to this document.</p>
              <KioskButton testId="kiosk-email-done" variant="outline" onClick={back}>Done</KioskButton>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="flex items-center gap-3 rounded-2xl glass border border-cyan-400/25 px-4 py-3 focus-within:border-cyan-400/60 transition">
                <Mail className="h-5 w-5 text-cyan-300" />
                <input
                  data-testid="kiosk-email-input"
                  type="email"
                  inputMode="email"
                  value={email}
                  onFocus={() => setSuppressIdle(true)}
                  onBlur={() => setSuppressIdle(false)}
                  onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
                  placeholder="Enter your email address"
                  className="w-full bg-transparent text-base text-white placeholder:text-slate-500 outline-none"
                />
              </div>
              {status === "error" && (
                <p className="text-sm text-rose-300" data-testid="kiosk-email-error">{message}</p>
              )}
              <KioskButton testId="kiosk-send-pdf-btn" type="submit" disabled={!valid || status === "sending"} className="w-full">
                {status === "sending" ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : <><Play className="h-5 w-5" /> Send PDF</>}
              </KioskButton>
              <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 text-cyan-400/70" />
                {branding?.email_privacy || "Your email will only be used for this request."}
              </div>
            </form>
          )}
        </div>
      </div>
    </ScreenShell>
  );
}
